import React, { useState } from "react";
import Swal from "sweetalert2";
import { useLanguage } from "../../../../../common/i18n/LanguageContext";

function Step3({
  isThaiTableVisible,
  rewardTypeGroups = [],
  maxfight,
  setMaxfight,
  maxfightUpline = {},
}) {
  const { t } = useLanguage();
  // Groups where "ใช้ค่าเดียวกันทั้งกลุ่ม" is checked — every stake's max
  // fight % in that group mirrors the group's first stake (each still
  // individually capped to its own upline value). While checked, only the
  // first stake's input stays editable; editing it cascades the value to
  // the rest of the group live.
  const [syncedGroupIds, setSyncedGroupIds] = useState({});

  // Max fight % entered for a code can never exceed the upline's own value
  // for that code (and never exceeds 100% regardless) — the upline hint
  // above the input is a promise, not just a suggestion.
  const clampMaxfight = (code, raw) => {
    const cap = maxfightUpline[code];
    const effectiveCap = cap !== undefined ? Math.min(cap, 100) : 100;
    return Math.min(raw, effectiveCap);
  };

  const cascadeToGroup = (group, rawValue) => {
    const rewardTypes = group.rewardTypes || [];
    setMaxfight((prev) => {
      const next = { ...prev };
      rewardTypes.forEach((rt) => {
        next[rt.code] = clampMaxfight(rt.code, rawValue);
      });
      return next;
    });
  };

  const handleToggleSync = (group, checked) => {
    setSyncedGroupIds((prev) => ({ ...prev, [group.id]: checked }));
    if (!checked) return;
    const firstRt = (group.rewardTypes || [])[0];
    if (!firstRt) return;
    cascadeToGroup(group, maxfight[firstRt.code] ?? 0);
  };

  const handleMaxfightChange = (e, group, rt) => {
    const raw = Number(e.target.value);
    const cap = maxfightUpline[rt.code];
    const effectiveCap = cap !== undefined ? Math.min(cap, 100) : 100;
    if (raw > effectiveCap) {
      Swal.fire({
        title: t("member_commission_validate_title"),
        text: `${t("commission_step_maxfight_exceed_prefix")} ${effectiveCap}`,
        icon: "error",
      });
    }
    const isFirst = (group.rewardTypes || [])[0]?.id === rt.id;
    if (syncedGroupIds[group.id] && isFirst) {
      cascadeToGroup(group, raw);
      return;
    }
    setMaxfight({ ...maxfight, [rt.code]: clampMaxfight(rt.code, raw) });
  };

  return (
    <div className="md:w-full h-auto flex p-2 flex-col space-y-2 bg-slate-200 rounded-lg">
      {rewardTypeGroups.length === 0 && (
        <div className="bg-white p-4 rounded-lg shadow-sm text-center text-slate-500">
          {t("member_commission_no_groups")}
        </div>
      )}

      {rewardTypeGroups.map((group) => {
        const firstRtId = (group.rewardTypes || [])[0]?.id;
        const synced = !!syncedGroupIds[group.id];
        return (
          <div
            key={group.id}
            className="w-full text-center justify p-1 rounded-sm bg-blue-400 hover:bg-blue-800"
          >
            <div className="flex items-center justify-center gap-2">
              <label className="flex items-center gap-1 text-white text-[15px] font-bold">
                <input
                  type="checkbox"
                  checked={synced}
                  onChange={(e) => handleToggleSync(group, e.target.checked)}
                />
                {t("member_commission_sync_group")}
              </label>
              <span className="text-white text-[15px] font-bold">
                {group.name}
              </span>
            </div>
            {isThaiTableVisible && (
              <div className="rounded overflow-hidden mt-1 bg-white p-2">
                {(group.rewardTypes || []).length === 0 && (
                  <span className="text-[12px] text-slate-400">
                    {t("commission_step_no_items")}
                  </span>
                )}
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2">
                  {(group.rewardTypes || []).map((rt) => {
                    const isFirst = rt.id === firstRtId;
                    const fieldDisabled = synced && !isFirst;
                    return (
                      <div
                        key={rt.id}
                        className="border-[1px] border-slate-300 rounded-lg p-2"
                        title={`${rt.code} (${rt.stake}) — ${rt.exp}`}
                      >
                        <div className="font-bold text-[12px] text-center mb-1">
                          {rt.name}
                        </div>
                        <div className="flex flex-col gap-1">
                          <div className="flex items-center gap-1">
                            <label className="text-[11px] text-slate-500 w-[70px] text-left shrink-0">
                              {t("commission_step_maxfight_label")}
                            </label>
                            <div className="flex flex-col w-full">
                              {maxfightUpline[rt.code] !== undefined && (
                                <span className="font-bold text-amber-500 justify-items-center items-center text-[11px] text-center">
                                  {maxfightUpline[rt.code]}%
                                </span>
                              )}
                              <input
                                type="number"
                                min="0"
                                max={Math.min(
                                  maxfightUpline[rt.code] ?? 100,
                                  100,
                                )}
                                disabled={fieldDisabled}
                                className="w-full font-bold text-[12px] border-[1px] border-slate-300 rounded-lg text-center disabled:bg-slate-100 disabled:text-slate-400"
                                name={rt.code}
                                value={maxfight[rt.code] ?? 0}
                                onChange={(e) =>
                                  handleMaxfightChange(e, group, rt)
                                }
                                onClick={(e) => e.target.select()}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default Step3;
